import * as XLSX from 'xlsx';
import { FieldAlias } from "../interfaces/field";
import Field from "../interfaces/field";

export interface ValidationResult {
    isValid: boolean;
    errors: string[];
    warnings: string[];
    data?: any[][];
}

export interface ValidationError {
    step: number;
    type: string;
    message: string;
    details?: any;
}

/**
 * Finds the column index for a given alias name in headers array
 * @param headers - Array of column headers from Excel file
 * @param aliasName - The alias name to search for
 * @returns Column index or -1 if not found
 */
export const findColumnIndex = (headers: string[], aliasName: string): number => {
    return headers.findIndex(header => 
        header === aliasName
    );
};

/**
 * Extracts unique non-empty values from a specific column
 * @param jsonData - Excel data as 2D array
 * @param columnIndex - Index of the column to extract from
 * @returns Set of unique string values
 */
export const extractUniqueValuesFromColumn = (jsonData: any[][], columnIndex: number): Set<string> => {
    const uniqueValues = new Set<string>();
    
    // Skip header row (start from index 1)
    for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i] as any[];
        
        if (columnIndex >= 0 && row[columnIndex]) {
            const value = String(row[columnIndex]).trim();
            if (value && value !== '') {
                uniqueValues.add(value);
            }
        }
    }
    
    return uniqueValues;
};

/**
 * Validates Excel file worksheet structure
 * @param workbook - XLSX workbook object
 * @returns Validation result for worksheet structure
 */
export const validateWorksheetName = (workbook: XLSX.WorkBook): ValidationResult => {
    const result: ValidationResult = {
        isValid: false,
        errors: [],
        warnings: []
    };
    
    if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
        result.errors.push('No worksheets found in the Excel file.');
        return result;
    }
    
    const hasSheet1 = workbook.SheetNames.includes('Sheet1');
    if (!hasSheet1) {
        result.errors.push(`Worksheet 'Sheet1' not found. Available sheets: ${workbook.SheetNames.join(', ')}. Please rename your worksheet to 'Sheet1'.`);
        return result;
    }
    
    result.isValid = true;
    return result;
};

/**
 * Processes Excel file with enhanced validation
 * @param file - Excel file to process
 * @returns Promise resolving to validation result with 2D array of Excel data
 */
export const processExcelDataWithValidation = (file: File): Promise<ValidationResult> => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            try {
                const data = e.target?.result;
                const workbook = XLSX.read(data, { type: 'binary' });
                
                // First validate worksheet structure
                const worksheetValidation = validateWorksheetName(workbook);
                if (!worksheetValidation.isValid) {
                    resolve(worksheetValidation);
                    return;
                }
                
                // Get Sheet1 specifically
                const worksheet = workbook.Sheets['Sheet1'];
                
                // Convert to JSON to extract data
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                
                if (jsonData.length === 0) {
                    resolve({
                        isValid: false,
                        errors: ['The Excel file is empty.'],
                        warnings: []
                    });
                    return;
                }
                
                resolve({
                    isValid: true,
                    errors: [],
                    warnings: [],
                    data: jsonData as any[][]
                });
            } catch (error) {
                resolve({
                    isValid: false,
                    errors: ['Error processing Excel file. Please ensure it\'s a valid Excel file.'],
                    warnings: []
                });
            }
        };
        
        reader.onerror = () => {
            resolve({
                isValid: false,
                errors: ['Error reading file.'],
                warnings: []
            });
        };
        
        reader.readAsBinaryString(file);
    });
};

